import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import * as XLSX from 'xlsx';
import { useToast } from "../../../hooks/use-toast";
import { BulkClaimAxiosInstance } from "../../../Network/BulkClaim";
import { SuccessRow, BulkResponse, FailedRow } from "../../../interface/BulkClaim";
import styles from './BulkClaim.module.css';

const BulkClaimValidation = () => {
  const { toast } = useToast();

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [bulkResponse, setBulkResponse] = useState<BulkResponse["data"] | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [responseMessage, setResponseMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'passed' | 'failed'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSuccessRows, setExpandedSuccessRows] = useState<Set<number>>(new Set());

  const itemsPerPage = 10;

  // ── Persist / restore ───────────────────────────────────────────────────────
  useEffect(() => {
    const saved = localStorage.getItem('bulkUploadResponse');
    const savedMsg = localStorage.getItem('bulkUploadMessage');
    if (saved) {
      try {
        setBulkResponse(JSON.parse(saved));
        if (savedMsg) setResponseMessage(savedMsg);
      } catch {
        console.error('Failed to restore saved results');
      }
    }
  }, []);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const toggleSuccessRow = (row: number) => {
    const next = new Set(expandedSuccessRows);
    next.has(row) ? next.delete(row) : next.add(row);
    setExpandedSuccessRows(next);
  };

  const handleReset = () => {
    setUploadedFile(null);
    setBulkResponse(null);
    setUploadProgress(0);
    setResponseMessage("");
    setExpandedSuccessRows(new Set());
    setActiveTab('all');
    setCurrentPage(1);
    setSearchQuery('');
    localStorage.removeItem('bulkUploadResponse');
    localStorage.removeItem('bulkUploadMessage');
  };

  // ── Download template ────────────────────────────────────────────────────────
  const downloadTemplate = async () => {
    try {
      const response = await BulkClaimAxiosInstance.get("claim-validation/template", {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BulkClaim_Template_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({ title: "Success", description: "Template downloaded successfully" });
    } catch (error: any) {
      const msg = error.response?.data?.message || "Failed to download template";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  // ── Upload ───────────────────────────────────────────────────────────────────
  const handleBulkUpload = async () => {
    if (!uploadedFile) {
      toast({ title: "Error", description: "Please select a file to upload", variant: "destructive" });
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', uploadedFile);

    let progressInterval: number | undefined;

    try {
      progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) { clearInterval(progressInterval); return 90; }
          return prev + 10;
        });
      }, 500);

      const response = await BulkClaimAxiosInstance.post<BulkResponse>(
        "claim-validation/bulk-upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          }
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      const payload = response.data?.data;
      const summary = payload?.summary;

      setBulkResponse(payload);
      localStorage.setItem('bulkUploadResponse', JSON.stringify(payload));

      const msg = `${summary?.total ?? 0} rows processed — ${summary?.successful ?? 0} passed, ${summary?.failed ?? 0} failed`;
      setResponseMessage(msg);
      localStorage.setItem('bulkUploadMessage', msg);

      toast({
        title: response.data?.success ? "Success" : "Completed with errors",
        description: msg,
        variant: response.data?.success ? undefined : "destructive",
      });

    } catch (error: any) {
      clearInterval(progressInterval);
      setUploadProgress(0);

      const status = error.response?.status;
      const errData = error.response?.data;

      if (status === 400) {
        const msg = errData?.error?.message || errData?.message || "Invalid file, missing Bayan token, or unsupported format";
        setResponseMessage(`Bad Request: ${msg}`);
        toast({ title: "Bad Request", description: msg, variant: "destructive" });
      } else if (status === 401) {
        setResponseMessage("Unauthorized: Invalid or expired token. Please login again.");
        toast({ title: "Unauthorized", description: "Invalid or expired token", variant: "destructive" });
      } else if (status === 403) {
        setResponseMessage("Forbidden: EMPLOYEE role required.");
        toast({ title: "Forbidden", description: "You need EMPLOYEE role to use this feature", variant: "destructive" });
      } else {
        const msg = errData?.error?.message || error.message || "Unknown error";
        setResponseMessage(`Upload failed: ${msg}`);
        toast({ title: "Error", description: msg, variant: "destructive" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Export ───────────────────────────────────────────────────────────────────
  const exportResults = () => {
    if (!bulkResponse) return;
 
    const rowsData: any[] = [];
 
    (bulkResponse.success || []).forEach((r) => {
      const rowNum = r.row;
      const { firstModuleChecks, secondModuleChecks } = r.data;
 
      const totalInternalChecks =
        firstModuleChecks.length +
        secondModuleChecks.activityChecks.length +
        secondModuleChecks.diagnosisChecks.length;
 
      if (totalInternalChecks === 0) {
        rowsData.push({
          "Row #": rowNum,
          "Row Status": "PASSED",
          "Check Type": "General",
          "Code / Identifier": "N/A",
          "Triggered Rule": "N/A",
          "Check Status": "PASS",
          "Message / Error Details": "All checks passed cleanly.",
          "Claim ID": "",
          "Activity Type": ""
        });
        return;
      }
 
      firstModuleChecks.forEach((check: any) => {
        rowsData.push({
          "Row #": rowNum,
          "Row Status": "PASSED",
          "Check Type": "First Module Check",
          "Code / Identifier": check.source || "BAYAN",
          "Triggered Rule": "API Connection / Request",
          "Check Status": "FAIL",
          "Message / Error Details": check.Message || check.message || check.ResponseStatus || "API Error occurred",
          "Claim ID": "",
          "Activity Type": ""
        });
      });
 
      secondModuleChecks.activityChecks.forEach((check: any) => {
        const isCheckPassed = check.Status === "PASSED" || check.Status === "PASS" || check.success;
        rowsData.push({
          "Row #": rowNum,
          "Row Status": "PASSED",
          "Check Type": "Activity Check",
          "Code / Identifier": check.activityCode || "N/A",
          "Triggered Rule": check.ruleName || check.ruleType || "General Check",
          "Check Status": isCheckPassed ? "PASS" : "FAIL",
          "Message / Error Details": check.Message || check.message || check.error || "No message",
          "Claim ID": "",
          "Activity Type": check.activityType || "CPT"
        });
      });
 
      secondModuleChecks.diagnosisChecks.forEach((check: any) => {
        const isCheckPassed = check.Status === "PASSED" || check.Status === "PASS" || check.success;
        rowsData.push({
          "Row #": rowNum,
          "Row Status": "PASSED",
          "Check Type": "Diagnosis Check",
          "Code / Identifier": check.diagnosisCode || "N/A",
          "Triggered Rule": check.ruleName || "ICD Configuration",
          "Check Status": isCheckPassed ? "PASS" : "FAIL",
          "Message / Error Details": check.Message || check.message || check.error || "No message",
          "Claim ID": "",
          "Activity Type": "ICD"
        });
      });
    });
 
    (bulkResponse.failed || []).forEach((r) => {
      const d = r.data || {};
      rowsData.push({
        "Row #": r.row,
        "Row Status": "FAILED",
        "Check Type": "Row Validation Error",
        "Code / Identifier": d.Activity_Code || "N/A",
        "Triggered Rule": "Row Structure / Parsing",
        "Check Status": "FAIL",
        "Message / Error Details": r.error || "Row level validation failed",
        "Claim ID": d.claim_Id || "",
        "Activity Type": d.Activity_Type || ""
      });
    });

    const ws = XLSX.utils.json_to_sheet(rowsData);
    
    const maxWd = [{ wch: 8 }, { wch: 12 }, { wch: 22 }, { wch: 20 }, { wch: 25 }, { wch: 12 }, { wch: 60 }, { wch: 15 }, { wch: 15 }];
    ws["!cols"] = maxWd;
 
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Validation Details");
    XLSX.writeFile(wb, `BulkValidation_Detailed_Results_${Date.now()}.xlsx`);
  };

  // ── Derived data for display ─────────────────────────────────────────────────
  const allRows: Array<{ type: 'success'; data: SuccessRow } | { type: 'failed'; data: FailedRow }> = [
    ...(bulkResponse?.success || []).map(r => ({ type: 'success' as const, data: r })),
    ...(bulkResponse?.failed || []).map(r => ({ type: 'failed' as const, data: r })),
  ].sort((a, b) => a.data.row - b.data.row);

  const filteredRows = allRows.filter(r => {
    const matchesTab =
      activeTab === 'all' ? true :
      activeTab === 'passed' ? r.type === 'success' :
      r.type === 'failed';

    if (!searchQuery) return matchesTab;

    const q = searchQuery.toLowerCase();

    const matchesRow = String(r.data.row).includes(q);
    if (matchesRow) return matchesTab;

    if (r.type === 'failed') {
      const d = r.data.data || {};
      return matchesTab && (
        (r.data.error || '').toLowerCase().includes(q) ||
        (d.claim_Id || '').toLowerCase().includes(q) ||
        (d.Primary_Diagnosis || '').toLowerCase().includes(q) ||
        (d.Activity_Code || '').toLowerCase().includes(q) ||
        (d.Activity_Type || '').toLowerCase().includes(q)
      );
    }

    if (r.type === 'success') {
      const { firstModuleChecks, secondModuleChecks } = r.data.data || { firstModuleChecks: [], secondModuleChecks: { activityChecks: [], diagnosisChecks: [] } };

      const matchesFirstModule = firstModuleChecks.some((check: any) => {
        const msg = check.Message || check.message || check.ResponseStatus || '';
        const src = check.source || '';
        return msg.toLowerCase().includes(q) || src.toLowerCase().includes(q);
      });
      if (matchesFirstModule) return matchesTab;

      const matchesActivity = secondModuleChecks.activityChecks?.some((check: any) => {
        const code = check.activityCode || '';
        const type = check.activityType || '';
        const rule = check.ruleName || check.ruleType || '';
        const msg = check.Message || check.message || check.error || '';
        return (
          code.toLowerCase().includes(q) ||
          type.toLowerCase().includes(q) ||
          rule.toLowerCase().includes(q) ||
          msg.toLowerCase().includes(q)
        );
      });
      if (matchesActivity) return matchesTab;

      const matchesDiagnosis = secondModuleChecks.diagnosisChecks?.some((check: any) => {
        const code = check.diagnosisCode || '';
        const msg = check.Message || check.message || check.error || '';
        const rule = check.ruleName || '';
        return (
          code.toLowerCase().includes(q) ||
          rule.toLowerCase().includes(q) ||
          msg.toLowerCase().includes(q)
        );
      });
      if (matchesDiagnosis) return matchesTab;
    }

    return false;
  });

  const totalPages = Math.ceil(filteredRows.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRows = filteredRows.slice(startIndex, startIndex + itemsPerPage);

  const summary = bulkResponse?.summary;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className={styles.screenWrapper} data-testid="BulkClaimValidation-page">
      <div className={styles.mainContainer} data-testid="BulkClaimValidation-page-container">

        {/* ── Upload card ── */}
        <div className={styles.uploadCard} data-testid="BulkClaimValidation-upload-card">
          <div className={styles.cardHeader}>
            <div className={styles.headerFlex}>
              <div className={styles.headerLeft}>
                <div className={styles.uploadIconBox}>
                  <i className="fas fa-cloud-upload-alt text-white"></i>
                </div>
                <div>
                  <h2 className={styles.headerTitle} data-testid="BulkClaimValidation-header-title">Upload Claims File</h2>
                  <p className={styles.headerSubtitle}>CSV, XLS, or XLSX </p>
                </div>
              </div>
              <Button
                data-testid="BulkClaimValidation-btn-download-template"
                onClick={downloadTemplate}
                className={styles.downloadBtn}
              >
                <i className="fas fa-download mr-2"></i>
                Download Template
              </Button>
            </div>
          </div>

          <div className={styles.cardBody}>
            <div className={styles.bodyFlexLayout}>
              <div className="flex-1">
                {/* Drop zone */}
                <label
                  data-testid="BulkClaimValidation-dropzone-label"
                  htmlFor="file-upload"
                  className={styles.dropZoneLabel}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-blue-500', 'bg-blue-50'); }}
                  onDragLeave={e => { e.preventDefault(); e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50'); }}
                  onDrop={e => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                    const file = e.dataTransfer.files?.[0];
                    if (file) {
                      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
                      if (['.xlsx', '.xls', '.csv'].includes(ext)) {
                        setUploadedFile(file);
                        toast({ title: "File Selected", description: `${file.name} is ready` });
                      } else {
                        toast({ title: "Invalid File", description: "Please use CSV, XLS, or XLSX", variant: "destructive" });
                      }
                    }
                  }}
                >
                  <div className={styles.dropZoneBorder}>
                    <div className={styles.dropZoneContentFlex}>
                      <div className={styles.dropZoneIconCircle}>
                        <i className="fas fa-cloud-upload-alt text-blue-600 text-2xl"></i>
                      </div>
                      <div className="flex-1 text-center">
                        <h3 className={styles.dropZoneTitle} data-testid="BulkClaimValidation-dropzone-title">
                          {uploadedFile ? (
                            <span className={styles.fileSuccessText} data-testid="BulkClaimValidation-file-success-name">
                              <i className="fas fa-check-circle"></i>
                              {uploadedFile.name}
                            </span>
                          ) : (
                            "Choose file or drag & drop here"
                          )}
                        </h3>
                        <p className={styles.dropZoneSupportedText}>Supported formats: Excel (.xlsx, .xls), CSV (.csv)</p>
                        {!uploadedFile && (
                          <div className={styles.browseFilesBtn} data-testid="BulkClaimValidation-btn-browse-files">
                            <i className="fas fa-folder-open mr-2"></i>
                            Browse Files
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </label>

                <input
                  data-testid="BulkClaimValidation-file-input"
                  id="file-upload"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setUploadedFile(file);
                      toast({ title: "File Selected", description: `${file.name} is ready` });
                    }
                  }}
                />

                {uploadedFile && (
                  <div className={styles.fileDetailsBadge} data-testid="BulkClaimValidation-file-details-badge">
                    <div className={styles.badgeFlexLayout}>
                      <div className={styles.badgeLeft}>
                        <div className={styles.fileBadgeIconBox}>
                          <i className="fas fa-file-alt text-white"></i>
                        </div>
                        <div>
                          <p className={styles.badgeFileName}>{uploadedFile.name}</p>
                          <p className={styles.badgeFileSize}>{(uploadedFile.size / 1024).toFixed(2)} KB</p>
                        </div>
                      </div>
                      <button
                        data-testid="BulkClaimValidation-btn-remove-selected-file"
                        onClick={() => {
                          setUploadedFile(null);
                          const el = document.getElementById('file-upload') as HTMLInputElement;
                          if (el) el.value = '';
                        }}
                        className={styles.removeFileBtn}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>

                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="mt-3" data-testid="BulkClaimValidation-upload-progress-container">
                        <div className={styles.progressTextFlex}>
                          <span className={styles.progressStatusText}>
                            <i className="fas fa-spinner fa-spin mr-1"></i>
                            Processing claims...
                          </span>
                          <span className={styles.progressPercentage} data-testid="BulkClaimValidation-progress-percentage">{uploadProgress}%</span>
                        </div>
                        <div className={styles.progressBarTrack}>
                          <div
                            className={styles.progressBarFill}
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <p className={styles.progressFooterText}>Please wait while we validate your claims...</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className={styles.actionButtonsSidebar}>
                <Button
                  data-testid="BulkClaimValidation-btn-validate"
                  onClick={handleBulkUpload}
                  disabled={!uploadedFile || isLoading}
                  className={styles.validateBtn}
                >
                  {isLoading ? (
                    <><i className="fas fa-spinner fa-spin mr-2"></i>Processing...</>
                  ) : (
                    <><i className="fas fa-rocket mr-2"></i>Validate</>
                  )}
                </Button>

                <Button
                  data-testid="BulkClaimValidation-btn-reset"
                  onClick={handleReset}
                  disabled={isLoading}
                  className={styles.resetBtn}
                >
                  <i className="fas fa-redo mr-2"></i>
                  Reset
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Status message ── */}
        {responseMessage && (
          <div data-testid="BulkClaimValidation-status-message-card" className={`${styles.statusMessageCard} ${
            responseMessage.toLowerCase().includes("failed") || responseMessage.toLowerCase().includes("bad request") || responseMessage.toLowerCase().includes("error")
              ? styles.statusCardError
              : styles.statusCardSuccess
          }`}>
            <div className={`${styles.statusIconBox} ${
              responseMessage.toLowerCase().includes("failed") || responseMessage.toLowerCase().includes("bad request") || responseMessage.toLowerCase().includes("error")
                ? styles.statusIconError : styles.statusIconSuccess
            }`}>
              <i className={`fas ${
                responseMessage.toLowerCase().includes("failed") || responseMessage.toLowerCase().includes("bad request") || responseMessage.toLowerCase().includes("error")
                  ? "fa-exclamation-circle text-red-600"
                  : "fa-check-circle text-green-600"
              } text-xl`}></i>
            </div>
            <p data-testid="BulkClaimValidation-status-text" className={`${styles.statusText} ${
              responseMessage.toLowerCase().includes("failed") || responseMessage.toLowerCase().includes("bad request") || responseMessage.toLowerCase().includes("error")
                ? styles.statusTextError : styles.statusTextSuccess
            }`}>
              {responseMessage}
            </p>
          </div>
        )}

        {/* ── Results ── */}
        {bulkResponse && (
          <div className={styles.responseContainer} data-testid="BulkClaimValidation-response-container">

            {/* Summary cards */}
            <div className={styles.summaryGrid} data-testid="BulkClaimValidation-summary-grid">
              <div className={styles.summaryCardBlue} data-testid="BulkClaimValidation-summary-total">
                <div className={styles.summaryCardFlex}>
                  <div>
                    <p className={styles.summaryCardLabel}>Total Rows</p>
                    <p className={styles.summaryCardValueGray}>{summary?.total ?? 0}</p>
                  </div>
                  <div className={styles.summaryIconBoxBlue}>
                    <i className="fas fa-file-medical text-blue-600 text-sm"></i>
                  </div>
                </div>
              </div>

              <div className={styles.summaryCardGreen} data-testid="BulkClaimValidation-summary-passed">
                <div className={styles.summaryCardFlex}>
                  <div>
                    <p className={styles.summaryCardLabel}>Passed</p>
                    <p className={styles.summaryCardValueGreen}>{summary?.successful ?? 0}</p>
                    <p className={styles.summaryCardPercentage}>
                      {summary?.total ? `${(((summary?.successful ?? 0) / summary.total) * 100).toFixed(1)}%` : '0%'}
                    </p>
                  </div>
                  <div className={styles.summaryIconBoxGreen}>
                    <i className="fas fa-check text-green-600 text-sm"></i>
                  </div>
                </div>
              </div>

              <div className={styles.summaryCardRed} data-testid="BulkClaimValidation-summary-failed">
                <div className={styles.summaryCardFlex}>
                  <div>
                    <p className={styles.summaryCardLabel}>Failed</p>
                    <p className={styles.summaryCardValueRed}>{summary?.failed ?? 0}</p>
                    <p className={styles.summaryCardPercentage}>
                      {summary?.total ? `${(((summary?.failed ?? 0) / summary.total) * 100).toFixed(1)}%` : '0%'}
                    </p>
                  </div>
                  <div className={styles.summaryIconBoxRed}>
                    <i className="fas fa-times text-red-600 text-sm"></i>
                  </div>
                </div>
              </div>

              <div className={styles.summaryCardPurple} data-testid="BulkClaimValidation-summary-success-rate">
                <div className={styles.summaryCardFlex}>
                  <div>
                    <p className={styles.summaryCardLabel}>Success Rate</p>
                    <p className={styles.summaryCardValuePurple}>
                      {summary?.total ? `${(((summary?.successful ?? 0) / summary.total) * 100).toFixed(0)}%` : '0%'}
                    </p>
                  </div>
                  <div className={styles.summaryIconBoxPurple}>
                    <i className="fas fa-chart-line text-purple-600 text-sm"></i>
                  </div>
                </div>
              </div>
            </div>

            {/* Export bar */}
            <div className={styles.exportBarCard} data-testid="BulkClaimValidation-export-card">
              <div className={styles.exportBarFlex}>
                <div className={styles.exportBarLeft}>
                  <div className={styles.exportExcelIconBox}>
                    <i className="fas fa-file-excel text-white"></i>
                  </div>
                  <div>
                    <h3 className={styles.exportBarTitle}>Export Results</h3>
                    <p className={styles.exportBarSubtitle}>Download validation results with all row data</p>
                  </div>
                </div>
                <Button
                  data-testid="BulkClaimValidation-btn-export-excel"
                  onClick={exportResults}
                  className={styles.exportExcelBtn}
                >
                  <i className="fas fa-download mr-2"></i>
                  Download Excel
                </Button>
              </div>
            </div>

            {/* Filter / search bar */}
            <div className={styles.filterBarCard} data-testid="BulkClaimValidation-filter-bar">
              <div className={styles.filterBarFlexLayout}>
                <div className="flex flex-wrap gap-2" data-testid="BulkClaimValidation-tabs-container">
                  {(['all', 'passed', 'failed'] as const).map(tab => (
                    <button
                      data-testid={`BulkClaimValidation-tab-${tab}`}
                      key={tab}
                      onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                      className={`px-5 py-2.5 rounded-lg font-semibold transition-all capitalize ${
                        activeTab === tab
                          ? tab === 'all' ? styles.tabActiveAll
                            : tab === 'passed' ? styles.tabActivePassed
                            : styles.tabActiveFailed
                          : styles.tabInactive
                      }`}
                    >
                      <i className={`fas ${tab === 'all' ? 'fa-list' : tab === 'passed' ? 'fa-check' : 'fa-times'} mr-2`}></i>
                      {tab === 'all' ? `All (${allRows.length})` : tab === 'passed' ? `Passed (${summary?.successful ?? 0})` : `Failed (${summary?.failed ?? 0})`}
                    </button>
                  ))}
                </div>

                <div className="relative flex-1 md:flex-initial" data-testid="BulkClaimValidation-search-wrapper">
                  <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    data-testid="BulkClaimValidation-search-input"
                    type="text"
                    placeholder="Search by row, claim ID, diagnosis..."
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    className={styles.searchInputField}
                  />
                  {searchQuery && (
                    <button
                      data-testid="BulkClaimValidation-btn-clear-search"
                      onClick={() => { setSearchQuery(''); setCurrentPage(1); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Row cards */}
            <div className={styles.rowsScrollContainer} data-testid="BulkClaimValidation-rows-container">
              {paginatedRows.length > 0 ? (
                paginatedRows.map((item, idx) => {
                  const isSuccess = item.type === 'success';
                  const rowNum = item.data.row;

                  if (isSuccess) {
                    const rowData = item.data as SuccessRow;
                    const isExpanded = expandedSuccessRows.has(rowNum);
                    const { firstModuleChecks, secondModuleChecks } = rowData.data;
                    const q = searchQuery ? searchQuery.toLowerCase() : "";

                    const filteredFirstModule = firstModuleChecks.filter((check: any) => {
                      if (!q) return true;
                      const msg = check.Message || check.message || check.ResponseStatus || '';
                      const src = check.source || '';
                      return msg.toLowerCase().includes(q) || src.toLowerCase().includes(q);
                    });

                    const filteredActivities = secondModuleChecks.activityChecks.filter((check: any) => {
                      if (!q) return true;
                      const code = check.activityCode || '';
                      const type = check.activityType || '';
                      const rule = check.ruleName || check.ruleType || '';
                      const msg = check.Message || check.message || check.error || '';
                      return (
                        code.toLowerCase().includes(q) ||
                        type.toLowerCase().includes(q) ||
                        rule.toLowerCase().includes(q) ||
                        msg.toLowerCase().includes(q)
                      );
                    });

                    const filteredDiagnoses = secondModuleChecks.diagnosisChecks.filter((check: any) => {
                      if (!q) return true;
                      const code = check.diagnosisCode || '';
                      const msg = check.Message || check.message || check.error || '';
                      const rule = check.ruleName || '';
                      return (
                        code.toLowerCase().includes(q) ||
                        rule.toLowerCase().includes(q) ||
                        msg.toLowerCase().includes(q)
                      );
                    });

                    const hasChecks =
                      filteredFirstModule.length > 0 ||
                      filteredActivities.length > 0 ||
                      filteredDiagnoses.length > 0;

                    return (
                      <div key={idx} className={styles.rowCardSuccessWrapper} data-testid={`BulkClaimValidation-success-row-${rowNum}`}>
                        {/* Header */}
                        <div className={styles.rowCardSuccessHeader}>
                          <div className={styles.rowCardHeaderLeftLayout}>
                            <div className={styles.rowCardNumberBadgeSuccess}>
                              #{startIndex + idx + 1}
                            </div>
                            <div>
                              <h3 className={styles.rowCardTitleFlex}>
                                <i className="fas fa-file-invoice text-green-600"></i>
                                Row {rowNum}
                              </h3>
                              <p className={styles.rowCardSubtitle}>
                                {filteredFirstModule.length} first-module · {filteredActivities.length} activity · {filteredDiagnoses.length} diagnosis checks shown
                              </p>
                            </div>
                          </div>
                          <div className={styles.rowCardHeaderRightLayout}>
                            <span className={styles.rowStatusBadgePassed}>
                              <i className="fas fa-check-circle mr-2"></i>PASSED
                            </span>
                            {hasChecks && (
                              <button
                                data-testid={`BulkClaimValidation-btn-toggle-row-${rowNum}`}
                                onClick={() => toggleSuccessRow(rowNum)}
                                className={styles.rowExpandToggleBtnSuccess}
                              >
                                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Expanded checks */}
                        {isExpanded && (
                          <div className="p-4 space-y-4" data-testid={`BulkClaimValidation-expanded-row-${rowNum}`}>
                            
                            {/* 1st Module Checks */}
                            {filteredFirstModule.length > 0 && (
                              <div>
                                <h4 className={styles.checksSectionTitleBlue}>
                                  <i className="fas fa-stethoscope text-blue-500"></i>
                                  First Module Checks ({filteredFirstModule.length})
                                </h4>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className={styles.tableHeaderRowBlue}>
                                        <th className={styles.tableHeaderCellIdxBlue}>#</th>
                                        <th className={styles.tableHeaderCellTextBlue}>Source / Error Message</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {filteredFirstModule.map((check: any, ci: number) => (
                                        <tr key={ci} className={styles.tableBodyRow}>
                                          <td className={styles.tableBodyCellIdx}>{ci + 1}</td>
                                          <td className={styles.tableBodyCellErrorText}>
                                            <span className="font-bold text-gray-700">[{check.source || "BAYAN"}]</span> {check.Message || check.message || check.ResponseStatus || "API Error occurred"}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {/* Activity Checks */}
                            {filteredActivities.length > 0 && (
                              <div>
                                <h4 className={styles.checksSectionTitlePurple}>
                                  <i className="fas fa-procedures text-purple-500"></i>
                                  Activity Checks ({filteredActivities.length})
                                </h4>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className={styles.tableHeaderRowPurple}>
                                        <th className={styles.tableHeaderCellTextLeftPurple}>Activity Info</th>
                                        <th className={styles.tableHeaderCellTextLeftPurple}>Triggered Rule</th>
                                        <th className={styles.tableHeaderCellTextCenterPurple}>Status</th>
                                        <th className={styles.tableHeaderCellTextLeftPurple}>Details / Message</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {filteredActivities.map((check: any, ci: number) => {
                                        const isCheckPassed = check.Status === "PASSED" || check.Status === "PASS" || check.success;
                                        return (
                                          <tr key={ci} className={styles.tableBodyRow}>
                                            <td className={styles.tableBodyCellActivityInfo}>
                                              {check.activityCode || "N/A"} <span className="text-xs text-gray-400">({check.activityType || "CPT"})</span>
                                            </td>
                                            <td className="py-2 px-3">
                                              <span className={styles.ruleBadgeAmber}>
                                                {check.ruleName || check.ruleType || "General Check"}
                                              </span>
                                            </td>
                                            <td className="py-2 px-3 text-center">
                                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${isCheckPassed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                <i className={`fas ${isCheckPassed ? 'fa-check' : 'fa-times'}`}></i>
                                                {isCheckPassed ? 'PASS' : 'FAIL'}
                                              </span>
                                            </td>
                                            <td className={`py-2 px-3 font-medium ${isCheckPassed ? 'text-gray-700' : 'text-red-600'}`}>
                                              {check.Message || check.message || check.error || "No message provided"}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {/* Diagnosis Checks */}
                            {filteredDiagnoses.length > 0 && (
                              <div>
                                <h4 className={styles.checksSectionTitleOrange}>
                                  <i className="fas fa-diagnoses text-orange-500"></i>
                                  Diagnosis Checks ({filteredDiagnoses.length})
                                </h4>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className={styles.tableHeaderRowOrange}>
                                        <th className={styles.tableHeaderCellTextLeftOrange}>ICD Code</th>
                                        <th className={styles.tableHeaderCellTextCenterOrange}>Status</th>
                                        <th className={styles.tableHeaderCellTextLeftOrange}>Details / Message</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {filteredDiagnoses.map((check: any, ci: number) => {
                                        const isCheckPassed = check.Status === "PASSED" || check.Status === "PASS" || check.success;
                                        return (
                                          <tr key={ci} className={styles.tableBodyRow}>
                                            <td className={styles.tableBodyCellIcdCode}>{check.diagnosisCode || "N/A"}</td>
                                            <td className="py-2 px-3 text-center">
                                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${isCheckPassed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                <i className={`fas ${isCheckPassed ? 'fa-check' : 'fa-times'}`}></i>
                                                {isCheckPassed ? 'PASS' : 'FAIL'}
                                              </span>
                                            </td>
                                            <td className={`py-2 px-3 font-medium ${isCheckPassed ? 'text-gray-700' : 'text-red-600'}`}>
                                              {check.Message || check.message || check.error || "No message provided"}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }

                  const rowData = item.data as FailedRow;
                  const d = rowData.data;

                  return (
                    <div key={idx} className={styles.rowCardFailedWrapper} data-testid={`BulkClaimValidation-failed-row-${rowData.row}`}>
                      <div className={styles.rowCardFailedHeader}>
                        <div className={styles.rowCardHeaderLeftLayout}>
                          <div className={styles.rowCardNumberBadgeFailed}>
                            #{startIndex + idx + 1}
                          </div>
                          <div>
                            <h3 className={styles.rowCardTitleFlex}>
                              <i className="fas fa-file-invoice text-red-600"></i>
                              Row {rowData.row}
                              {d.claim_Id && <span className={styles.rowCardClaimIdText}>· {d.claim_Id}</span>}
                            </h3>
                            <p className={styles.rowCardErrorSubtitle}>
                              <i className="fas fa-exclamation-circle"></i>
                              {rowData.error}
                            </p>
                          </div>
                        </div>
                        <span className={styles.rowStatusBadgeFailed}>
                          <i className="fas fa-times-circle mr-2"></i>FAILED
                        </span>
                      </div>

                      {/* Failed row data */}
                      <div className="p-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {[
                            { label: "Claim ID", value: d.claim_Id },
                            { label: "Date", value: d.current_Date },
                            { label: "Primary Diagnosis", value: d.Primary_Diagnosis },
                            { label: "Activity Code", value: d.Activity_Code },
                            { label: "Activity Type", value: d.Activity_Type },
                            { label: "Patient ID Type", value: d.patient_Identifier_Type },
                            { label: "Patient ID Value", value: d.patient_Identifier_Value },
                            { label: "Activity ID", value: d.Activity_Id },
                          ].map(({ label, value }) =>
                            value ? (
                              <div key={label} className={styles.failedDataBadgeCard}>
                                <p className={styles.failedDataBadgeLabel}>{label}</p>
                                <p className={styles.failedDataBadgeValue} title={value}>{value}</p>
                              </div>
                            ) : null
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className={styles.noRowsEmptyCard} data-testid="BulkClaimValidation-no-rows-card">
                  <div className={styles.noRowsIconCircle}>
                    <i className="fas fa-inbox text-4xl text-gray-300"></i>
                  </div>
                  <h3 className={styles.noRowsTitle}>No Rows Found</h3>
                  <p className="text-gray-600">No rows match your current filter criteria</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={styles.paginationCard} data-testid="BulkClaimValidation-pagination-container">
                <div className={styles.paginationFlexLayout}>
                  <p className={styles.paginationStatsText}>
                    Showing <span className="font-bold text-gray-800">{startIndex + 1}</span> to <span className="font-bold text-gray-800">{Math.min(startIndex + itemsPerPage, filteredRows.length)}</span> of <span className="font-bold text-gray-800">{filteredRows.length}</span> results
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="BulkClaimValidation-btn-prev-page"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={styles.paginationNavBtn}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;

                      return (
                        <button
                          data-testid={`BulkClaimValidation-btn-page-${pageNum}`}
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                            currentPage === pageNum
                              ? styles.paginationPageBtnActive
                              : styles.paginationPageBtnInactive
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      data-testid="BulkClaimValidation-btn-next-page"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className={styles.paginationNavBtn}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkClaimValidation;